/**
 * <shipping-calculator> — FreteBarato shipping estimate on the product page.
 *
 * Reads the currently selected variant + quantity from the product form, posts a
 * Shopify CarrierService `rate` payload to the Cloudflare Worker proxy, and renders
 * the returned carrier options. Estimate only — does not affect checkout shipping.
 *
 * Config comes from data-* attributes set by blocks/shipping-calculator.liquid.
 */

const CEP_KEY = 'fb_frete_cep';
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

class ShippingCalculator extends HTMLElement {
  connectedCallback() {
    this.cep = this.querySelector('[ref="cep"]');
    this.submit = this.querySelector('[ref="submit"]');
    this.msg = this.querySelector('[ref="msg"]');
    this.results = this.querySelector('[ref="results"]');

    this.workerUrl = (this.dataset.workerUrl || '').trim();
    this.box = {
      length: toInt(this.dataset.length, 20),
      width: toInt(this.dataset.width, 15),
      height: toInt(this.dataset.height, 10),
      grams: toInt(this.dataset.defaultGrams, 500),
    };
    this.productTitle = this.dataset.productTitle || 'Produto';
    this.variants = this.parseVariants();

    // Restore last CEP for convenience.
    try {
      const saved = localStorage.getItem(CEP_KEY);
      if (saved && this.cep) this.cep.value = maskCep(saved);
    } catch (_) {}

    if (this.cep) {
      this.cep.addEventListener('input', () => {
        this.cep.value = maskCep(this.cep.value);
      });
      this.cep.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.calculate();
        }
      });
    }
    if (this.submit) {
      this.submit.addEventListener('click', () => this.calculate());
    }
  }

  parseVariants() {
    const node = this.querySelector('.fb-frete__variants');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (_) {
      return [];
    }
  }

  /** Variant currently selected in the product form, falling back to the first. */
  currentVariant() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('input[name="id"]');
    const id = input ? Number(input.value) : NaN;
    return this.variants.find((v) => v.id === id) || this.variants[0] || null;
  }

  currentQuantity() {
    const scope = this.closest('.shopify-section') || document;
    const input = scope.querySelector('input[name="quantity"]');
    const q = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  async calculate() {
    if (this.results) this.results.innerHTML = '';
    const digits = (this.cep?.value || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      return this.showMessage('Digite um CEP válido (8 dígitos).', 'error');
    }
    if (!this.workerUrl) {
      console.error('[shipping-calculator] worker_url não configurado no bloco.');
      return this.showMessage('Cálculo de frete indisponível no momento.', 'error');
    }
    const variant = this.currentVariant();
    if (!variant) {
      return this.showMessage('Selecione uma variante.', 'error');
    }

    try {
      localStorage.setItem(CEP_KEY, digits);
    } catch (_) {}

    const payload = {
      rate: {
        destination: { country: 'BR', postal_code: digits },
        items: [
          {
            name: this.productTitle,
            sku: variant.sku || String(variant.id),
            quantity: this.currentQuantity(),
            grams: variant.grams > 0 ? variant.grams : this.box.grams,
            price: variant.price, // unit price in cents
            requires_shipping: true,
            // dimensions (cm) — single fixed box for all products
            length: this.box.length,
            width: this.box.width,
            height: this.box.height,
          },
        ],
        currency: 'BRL',
      },
    };

    this.setBusy(true);
    this.showMessage('Calculando…', 'info');
    this.results.innerHTML = '';
    try {
      const res = await fetch(this.workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const err = data && data.error ? data.error : 'Não foi possível calcular o frete.';
        return this.showMessage(err, 'error');
      }
      const rates = (data && data.rates) || [];
      if (rates.length === 0) {
        return this.showMessage('Sem opções de entrega para este CEP.', 'info');
      }
      this.renderRates(rates);
      this.hideMessage();
    } catch (err) {
      console.error('[shipping-calculator]', err);
      this.showMessage('Erro ao calcular o frete. Tente novamente.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  renderRates(rates) {
    const frag = document.createDocumentFragment();
    rates
      .slice()
      .sort((a, b) => (a.total_price || 0) - (b.total_price || 0))
      .forEach((rate) => {
        const li = document.createElement('li');
        li.className = 'fb-frete__item';

        const left = document.createElement('span');
        const name = document.createElement('span');
        name.className = 'fb-frete__name';
        name.textContent = rate.service_name || rate.name || 'Entrega';
        left.appendChild(name);

        const eta = etaLabel(rate.min_delivery_date, rate.max_delivery_date);
        if (eta) {
          const span = document.createElement('span');
          span.className = 'fb-frete__eta';
          span.textContent = ' · ' + eta;
          left.appendChild(span);
        }

        const price = document.createElement('span');
        price.className = 'fb-frete__price';
        const cents = Number(rate.total_price);
        price.textContent = Number.isFinite(cents) ? BRL.format(cents / 100) : '—';

        li.append(left, price);
        frag.appendChild(li);
      });
    this.results.innerHTML = '';
    this.results.appendChild(frag);
  }

  setBusy(busy) {
    if (!this.submit) return;
    this.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.submit.disabled = busy;
  }

  showMessage(text, state) {
    if (!this.msg) return;
    this.msg.textContent = text;
    this.msg.dataset.state = state || 'info';
    this.msg.hidden = false;
  }

  hideMessage() {
    if (this.msg) this.msg.hidden = true;
  }
}

function toInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function maskCep(value) {
  const d = String(value).replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? d.slice(0, 5) + '-' + d.slice(5) : d;
}

/** Business-day estimate from today to the delivery dates. */
function etaLabel(minDate, maxDate) {
  const min = daysUntil(minDate);
  const max = daysUntil(maxDate);
  if (max == null && min == null) return '';
  const lo = min ?? max;
  const hi = max ?? min;
  if (lo === hi) return `${hi} ${hi === 1 ? 'dia útil' : 'dias úteis'}`;
  return `${lo}–${hi} dias úteis`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target)) return null;
  const today = new Date();
  const a = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(1, Math.round((a - b) / 86400000));
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
